// src/contexts/nobishiro-dashboard/PlateauGrowthAnalysis.jsx (周期的成長モデル 最終完成版)
import React, { useState, useMemo } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, ReferenceArea } from 'recharts';
import { TrendingUp, Rocket, Sprout } from 'lucide-react';
import { diagnoseLearningType } from '../../services/aiAnalysisService.js';
import { coachingAdvice } from '../../data/coachingAdvice.js';

// 周期的・フラクタルな成長データをシミュレーション生成する最終版関数
const generateFractalGrowthCurve = (growthProfile, durationMonths) => {
  const data = [];
  const targetMastery = 90; // 最終到達点
  
  // ユーザーの特性値 (1-5スケールを0.5-1.5の係数に変換)
  const confidenceFactor = 0.5 + ((growthProfile?.q1 || 3) - 1) / 4; // 0.5 to 1.5
  const resilienceFactor = 0.5 + ((growthProfile?.q6 || 3) - 1) / 4; // 0.5 to 1.5

  for (let i = 0; i <= durationMonths; i++) {
    // 1. 実際の能力 (S字曲線)
    const competence = targetMastery / (1 + Math.exp(-0.15 * (i - durationMonths * 0.5)));

    // 2. 自己認識（自信）- 能力曲線に周期的な波を追加
    // 期間が短いほど波の周波数が高くなる
    const frequency = Math.PI * 2 / (Math.max(6, durationMonths / 3)); 
    // 序盤ほど自信と能力の乖離（振幅）が大きく、回復力で変動が抑えられる
    const amplitude = (25 / resilienceFactor) * Math.exp(-0.08 * i) * confidenceFactor;
    
    let selfPerception = competence + amplitude * Math.sin(frequency * i - Math.PI / 2);

    // 最初のピークを強調
    if (i < durationMonths * 0.25) {
        selfPerception += 10 * Math.exp(-0.3 * i) * confidenceFactor;
    }

    selfPerception = Math.max(0, Math.min(100, selfPerception));
    
    data.push({ 
      month: i, 
      name: `${Math.round(i)}ヶ月`,
      "実際の能力": parseFloat(competence.toFixed(1)), 
      "自己認識(自信)": parseFloat(selfPerception.toFixed(1)) 
    });
  }
  return data;
};

// --- ここから先のコンポーネント定義は前回と同じですが、完全なコードとして記載します ---

const PlateauGrowthAnalysis = ({ profile }) => {
  const [duration, setDuration] = useState(36);
  
  const curveData = useMemo(() => generateFractalGrowthCurve(profile.growthPatternProfile, duration), [profile.growthPatternProfile, duration]);
  const learningTypeKey = useMemo(() => diagnoseLearningType(profile.growthPatternProfile), [profile.growthPatternProfile]);
  const personalizedAdvice = coachingAdvice[learningTypeKey];
  const icons = { Rocket, Sprout };
  const AdviceIcon = icons[personalizedAdvice.icon] || Sprout;

  const timeRanges = [
      { label: '3週間', months: 0.75 }, { label: '100日', months: 3.3 },
      { label: '半年', months: 6 }, { label: '9ヶ月', months: 9 },
      { label: '1年', months: 12 }, { label: '3年', months: 36 },
  ];

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-xl shadow-sm p-6">
        <h2 className="text-xl font-bold text-gray-800 mb-2">{profile.username}さんの成長パターン分析</h2>
        <p className="text-gray-600">あなたの学習スタイルに基づき、「実際の能力」と「自信の推移」をシミュレーションします。</p>
      </div>
      
      <div className="bg-white rounded-xl shadow-sm p-3">
        <div className="flex items-center justify-center flex-wrap gap-2">
            {timeRanges.map(range => (
                <button key={range.label} onClick={() => setDuration(range.months)} className={`px-4 py-2 text-sm font-semibold rounded-lg transition-all duration-200 ${Math.abs(duration - range.months) < 0.1 ? 'bg-blue-600 text-white shadow-md' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}>
                    {range.label}
                </button>
            ))}
        </div>
      </div>
      
      <div className="bg-white rounded-xl shadow-sm p-6">
        <div className="h-96 w-full">
          <ResponsiveContainer>
            <LineChart data={curveData} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" type="category" allowDuplicatedCategory={false} tick={{ fontSize: 12 }} />
              <YAxis domain={[0, 110]} unit="%" />
              <Tooltip />
              <Legend verticalAlign="top" height={36}/>
              <Line type="monotone" dataKey="実際の能力" stroke="#16a34a" strokeWidth={3} dot={false} activeDot={{ r: 6 }} />
              <Line type="monotone" dataKey="自己認識(自信)" stroke="#3b82f6" strokeWidth={2} strokeDasharray="5 5" dot={false} activeDot={{ r: 6 }} />
            </LineChart>
          </ResponsiveContainer>
        </div>
        <div className="mt-4 flex items-center justify-center gap-6 text-xs text-gray-600">
            <div className="flex items-center gap-2"><div className="w-4 h-1 bg-green-500 rounded-full"></div><span>実際の能力</span></div>
            <div className="flex items-center gap-2"><div className="w-4 h-1 border-t-2 border-b-2 border-dotted border-blue-500"></div><span>自己認識(自信)</span></div>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm p-6">
        <div className="flex items-center gap-3 mb-4"><div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center"><AdviceIcon className="w-6 h-6 text-purple-600" /></div><div><p className="text-sm font-medium text-purple-700">あなたの学習タイプ</p><h3 className="text-lg font-semibold text-gray-900">{personalizedAdvice.name}</h3></div></div>
        <h4 className="font-semibold text-gray-800 mb-2">{personalizedAdvice.title}</h4>
        <ul className="space-y-2 text-gray-700 text-sm">{personalizedAdvice.advice.map((text, index) => (<li key={index} className="flex items-start gap-3"><div className="w-2 h-2 bg-purple-500 rounded-full mt-1.5 flex-shrink-0"></div><span>{text}</span></li>))}</ul>
      </div>
    </div>
  );
};

export default PlateauGrowthAnalysis;
